import { languageDisplayLabel } from '@/api/enums/languages.enum';
import { FiltersType, ListingRequest } from '@/api/types';
import { client } from '@/client';
import { AccountLanguage } from '@/client/accounts';
import { PartialRequestMultiselect } from '@/components/form/generic/PartialRequestMultiselect';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import React, { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

type UserLanguagesMultiselectInputProps = {
  onChange: (value: AccountLanguage[]) => void;
  onBlur?: () => void;
  additionalFilters?: FiltersType;
  selectedOptions?: AccountLanguage[];
  isDisabled?: boolean;
  accountId?: string;
} & Omit<HTMLAttributes<HTMLSelectElement>, 'onChange' | 'defaultChecked'>;

export const UserLanguagesMultiselectInput: React.FC<
  UserLanguagesMultiselectInputProps
> = ({
  onChange,
  onBlur,
  className,
  additionalFilters,
  selectedOptions,
  isDisabled,
  accountId,
}) => {
  const { t } = useTranslation();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const fetchLanguages = isDisabled
    ? undefined
    : async (params: ListingRequest) =>
        await client.accountEntities.getLanguages(
          params,
          accountId || currentAccount?.id || '',
        );

  return (
    <PartialRequestMultiselect
      dataKey="name"
      fetchService={fetchLanguages}
      filter
      optionLabel="name"
      optionValue="value"
      placeholder={t('generic.select')}
      maxSelectedLabels={0}
      selectedItemsLabel={t('generic.itemsSelected.count')}
      additionalFilters={additionalFilters}
      selectedOptions={selectedOptions}
      className={className}
      handleOnChange={onChange}
      onBlur={onBlur}
      disabled={isDisabled}
      isNotListingResponse
      customDataMap={(item: { name: string; value: AccountLanguage }) => ({
        ...item,
        name:
          languageDisplayLabel[
            item.name as keyof typeof languageDisplayLabel
          ] ?? item.name,
      })}
      customLabelMap={(item: { name: string }) =>
        languageDisplayLabel[item.name as keyof typeof languageDisplayLabel] ??
        item.name
      }
    />
  );
};
